import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertTriangle, TrendingUp, Package, DollarSign, Trash2, Sparkles, ChefHat,
  Truck, Mail, Download, Send,
} from "lucide-react";

const severityColors = {
  critical: "bg-[#e11d48] hover:bg-[#e11d48]/80",
  high: "bg-[#f59e0b] hover:bg-[#f59e0b]/80",
  medium: "bg-[#3b82f6] hover:bg-[#3b82f6]/80",
};

export default function AdminInventoryDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [window, setWindow] = useState(7);
  const [suppliers, setSuppliers] = useState([]);
  // Weekly digest
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestData, setDigestData] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async (w = window) => {
    setLoading(true);
    try {
      const [{ data: dash }, { data: sup }] = await Promise.all([
        api.get(`/admin/inventory/dashboard?window=${w}`),
        api.get(`/admin/inventory/suppliers`),
      ]);
      setData(dash);
      setSuppliers(sup?.suppliers || []);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(window); /* eslint-disable-next-line */ }, [window]);

  const openDigest = async () => {
    setDigestOpen(true);
    setDigestLoading(true);
    try {
      const { data } = await api.post("/admin/inventory/weekly-report/preview");
      setDigestData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDigestLoading(false);
    }
  };

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/inventory/weekly-report/send");
      if (data.sent) toast.success("Informe enviado a tu correo");
      else toast.info("Sin compras urgentes — no se envió email");
      setDigestOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSendingDigest(false);
    }
  };

  const exportContpaqi = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/inventory/export/contpaqi?days=30", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orden_movimientos_${new Date().toISOString().slice(0, 10)}.csv`;
      a.rel = "noopener";
      a.target = "_self";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch { /* noop */ }
        URL.revokeObjectURL(url);
      }, 1500);
      toast.success("CSV listo · compatible CONTPAQi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setExporting(false);
    }
  };

  if (loading && !data) return <Card className="p-8 text-center text-[#7248ae]">Calculando predicciones…</Card>;
  if (!data) return null;

  const k = data.kpis || {};

  return (
    <div className="space-y-6" data-testid="admin-inventory-dashboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#1a0f2e] flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-[#7248ae]" />
            Predicción de Compras
          </h1>
          <p className="text-[#7248ae] text-sm">Motor de IA · análisis de consumo y compras inteligentes</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            onClick={openDigest}
            className="border-[#3e166c]/40 text-[#3e166c] hover:bg-[#3e166c]/10"
            data-testid="weekly-digest-button"
          >
            <Mail className="h-4 w-4 mr-2" />Informe semanal
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={exportContpaqi}
            disabled={exporting}
            className="border-[#10b981]/40 text-[#10b981] hover:bg-[#10b981]/10"
            data-testid="export-contpaqi-button"
          >
            <Download className="h-4 w-4 mr-2" />{exporting ? "Exportando…" : "Export CONTPAQi"}
          </Button>
          <span className="text-xs text-[#7248ae] uppercase tracking-widest ml-2">Horizonte</span>
          {[7, 14, 30].map(w => (
            <Button
              key={w}
              size="sm"
              variant={window === w ? "default" : "outline"}
              onClick={() => setWindow(w)}
              className={window === w ? "bg-[#3e166c] hover:bg-[#3e166c]/90" : ""}
              data-testid={`window-${w}-button`}
            >
              {w}d
            </Button>
          ))}
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="inventory-kpis">
        <Kpi label="Ingredientes" value={k.ingredients_count ?? 0} icon={Package} color="#3e166c" />
        <Kpi label="Alertas activas" value={k.alerts_count ?? 0} icon={AlertTriangle} color={(k.alerts_count ?? 0) > 0 ? "#e11d48" : "#10b981"} />
        <Kpi label="Valor inventario" value={formatCurrency(k.inventory_value ?? 0)} icon={DollarSign} color="#10b981" />
        <Kpi label={`Compra sugerida ${window}d`} value={formatCurrency(k.suggested_purchase_cost_7d ?? 0)} icon={TrendingUp} color="#7248ae" />
        <Kpi label="Merma 30d" value={formatCurrency(k.waste_value_30d ?? 0)} icon={Trash2} color="#f59e0b" />
      </div>

      {/* Alerts */}
      <Card className="bg-white border-[#b182ec]/30 p-5" data-testid="dashboard-alerts">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl text-[#1a0f2e] flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[#e11d48]" />
            Alertas
          </h2>
          <Link to="/admin/inventory" className="text-xs text-[#7248ae] underline">Ir a inventario</Link>
        </div>
        {(data.alerts || []).length === 0 ? (
          <p className="text-sm text-[#10b981]">Todo en orden, no hay alertas activas.</p>
        ) : (
          <div className="space-y-2">
            {data.alerts.map((a, i) => (
              <div key={i} className="flex items-center justify-between bg-[#fcfbfd] rounded-xl px-3 py-2 border border-[#b182ec]/20" data-testid={`alert-row-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Badge className={severityColors[a.severity] || "bg-[#94a3b8]"}>{a.severity}</Badge>
                  <span className="text-sm text-[#1a0f2e] truncate">{a.message}</span>
                </div>
                {a.suggested_purchase_qty > 0 && (
                  <span className="text-xs text-[#7248ae] whitespace-nowrap">
                    Comprar {a.suggested_purchase_qty} {a.unit} · {formatCurrency(a.cost)}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Predictions table */}
      <Card className="bg-white border-[#b182ec]/30 p-5" data-testid="dashboard-predictions">
        <h2 className="font-display text-xl text-[#1a0f2e] mb-3 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#7248ae]" />
          Predicción por ingrediente
        </h2>
        {(data.predictions || []).length === 0 ? (
          <p className="text-sm text-[#7248ae]">Agrega ingredientes y configura recetas para empezar a predecir.</p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[#7248ae] tracking-widest">
                  <th className="px-2 py-2">Ingrediente</th>
                  <th className="px-2 py-2 text-right">Stock</th>
                  <th className="px-2 py-2 text-right">Consumo/día</th>
                  <th className="px-2 py-2 text-right">Tendencia</th>
                  <th className="px-2 py-2 text-right">Días restantes</th>
                  <th className="px-2 py-2 text-right">Comprar ({window}d)</th>
                  <th className="px-2 py-2 text-right">Costo</th>
                </tr>
              </thead>
              <tbody>
                {data.predictions.map(p => (
                  <tr key={p.ingredient_id} className="border-t border-[#b182ec]/20" data-testid={`pred-row-${p.ingredient_id}`}>
                    <td className="px-2 py-2 text-[#1a0f2e] font-medium">{p.ingredient_name}</td>
                    <td className="px-2 py-2 text-right text-[#1a0f2e]">{p.stock} <span className="text-xs text-[#7248ae]">{p.unit}</span></td>
                    <td className="px-2 py-2 text-right text-[#1a0f2e]">{p.avg_daily_7d}</td>
                    <td className="px-2 py-2 text-right">
                      {p.weekly_growth_pct == null ? <span className="text-[#7248ae]">—</span> : (
                        <span className={p.weekly_growth_pct > 5 ? "text-[#e11d48]" : p.weekly_growth_pct < -5 ? "text-[#10b981]" : "text-[#7248ae]"}>
                          {p.weekly_growth_pct > 0 ? "+" : ""}{p.weekly_growth_pct}%
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {p.days_until_stockout == null ? <span className="text-[#7248ae]">∞</span> : (
                        <span className={p.days_until_stockout <= 3 ? "text-[#e11d48] font-medium" : p.days_until_stockout <= 7 ? "text-[#f59e0b]" : "text-[#10b981]"}>
                          {p.days_until_stockout}d
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-[#1a0f2e]">{p.suggested_purchase_qty} <span className="text-xs text-[#7248ae]">{p.unit}</span></td>
                    <td className="px-2 py-2 text-right text-[#1a0f2e]">{formatCurrency(p.suggested_purchase_cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Top consumed + margins */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="bg-white border-[#b182ec]/30 p-5" data-testid="dashboard-top-consumed">
          <h3 className="font-display text-lg text-[#1a0f2e] mb-3">Top consumidos (7d)</h3>
          {(data.top_consumed || []).length === 0 ? (
            <p className="text-sm text-[#7248ae]">Sin datos suficientes aún.</p>
          ) : (
            <div className="space-y-2">
              {data.top_consumed.map(t => (
                <div key={t.ingredient_id} className="flex items-center justify-between border-b border-[#b182ec]/20 pb-1">
                  <span className="text-sm text-[#1a0f2e]">{t.ingredient_name}</span>
                  <span className="text-xs text-[#7248ae]">{t.avg_daily_7d} {t.unit}/día</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="bg-white border-[#b182ec]/30 p-5" data-testid="dashboard-dish-margins">
          <h3 className="font-display text-lg text-[#1a0f2e] mb-3 flex items-center gap-2">
            <ChefHat className="h-4 w-4 text-[#7248ae]" />Márgenes por platillo
          </h3>
          {(data.dish_margins || []).length === 0 ? (
            <p className="text-sm text-[#7248ae]">Crea recetas para calcular márgenes.</p>
          ) : (
            <div className="space-y-2">
              {data.dish_margins.map(d => (
                <div key={d.dish_id} className="flex items-center justify-between text-sm">
                  <span className="text-[#1a0f2e] truncate flex-1">{d.name}</span>
                  <span className="text-[#7248ae] text-xs mr-3">{formatCurrency(d.cost)} → {formatCurrency(d.price)}</span>
                  <span className={`font-display ${d.margin_pct >= 60 ? "text-[#10b981]" : d.margin_pct >= 30 ? "text-[#f59e0b]" : "text-[#e11d48]"}`}>{d.margin_pct}%</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {(data.waste_breakdown || []).length > 0 && (
        <Card className="bg-white border-[#b182ec]/30 p-5" data-testid="dashboard-waste">
          <h3 className="font-display text-lg text-[#1a0f2e] mb-3 flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-[#f59e0b]" />Merma últimos 30 días
          </h3>
          <div className="space-y-1">
            {data.waste_breakdown.map((w, i) => (
              <div key={i} className="flex justify-between text-sm border-b border-[#b182ec]/20 pb-1">
                <span className="text-[#1a0f2e]">{w.ingredient_name}</span>
                <span className="text-[#7248ae]">{w.qty} {w.unit} · {formatCurrency(w.cost)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Suppliers */}
      {suppliers.length > 0 && (
        <Card className="bg-white border-[#b182ec]/30 p-5" data-testid="dashboard-suppliers">
          <h3 className="font-display text-lg text-[#1a0f2e] mb-3 flex items-center gap-2">
            <Truck className="h-4 w-4 text-[#7248ae]" />Proveedores (últimos 90 días)
          </h3>
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[#7248ae] tracking-widest">
                  <th className="px-2 py-2">Proveedor</th>
                  <th className="px-2 py-2 text-right">Inversión 90d</th>
                  <th className="px-2 py-2 text-right">Compras</th>
                  <th className="px-2 py-2 text-right">Ingredientes</th>
                  <th className="px-2 py-2 text-right">Última compra</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.map((s, i) => (
                  <tr key={i} className="border-t border-[#b182ec]/20" data-testid={`supplier-row-${i}`}>
                    <td className="px-2 py-2 text-[#1a0f2e] font-medium">{s.supplier}</td>
                    <td className="px-2 py-2 text-right text-[#1a0f2e]">{formatCurrency(s.total_spend_90d)}</td>
                    <td className="px-2 py-2 text-right text-[#1a0f2e]">{s.purchase_count}</td>
                    <td className="px-2 py-2 text-right text-[#1a0f2e]">{s.unique_ingredients}</td>
                    <td className="px-2 py-2 text-right text-[#7248ae] text-xs">
                      {s.last_purchase_at ? s.last_purchase_at.slice(0, 10) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Weekly digest dialog */}
      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="max-w-lg" data-testid="weekly-digest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#3e166c]" />
              Informe de compras inteligente
            </DialogTitle>
          </DialogHeader>
          {digestLoading ? (
            <p className="text-sm text-[#7248ae] py-6 text-center">Calculando recomendación…</p>
          ) : digestData ? (
            <div className="space-y-3">
              <p className="text-sm text-[#7248ae]">
                Basado en tus últimos 30 días, el motor recomienda comprar:
              </p>
              {digestData.top_items.length === 0 ? (
                <Card className="p-4 bg-[#10b981]/10 border-[#10b981]/30 text-sm text-[#065f46]">
                  Todo en orden, no hay compras urgentes esta semana.
                </Card>
              ) : (
                <div className="space-y-2" data-testid="digest-top-items">
                  {digestData.top_items.map((t, i) => (
                    <div key={i} className="flex items-center justify-between bg-[#fcfbfd] rounded-xl px-3 py-2 border border-[#b182ec]/20">
                      <div className="flex items-center gap-2">
                        <span className="font-display text-[#3e166c]">#{i + 1}</span>
                        <span className="text-sm text-[#1a0f2e]">{t.name}</span>
                        {t.days_until_stockout != null && (
                          <Badge className={t.days_until_stockout <= 3 ? "bg-[#e11d48]" : t.days_until_stockout <= 7 ? "bg-[#f59e0b]" : "bg-[#10b981]"}>
                            {t.days_until_stockout}d
                          </Badge>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#7248ae]">{t.qty} {t.unit}</p>
                        <p className="text-sm font-display text-[#1a0f2e]">{formatCurrency(t.cost)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="rounded-xl bg-[#3e166c] p-4 flex items-center justify-between text-white">
                <span className="text-xs uppercase tracking-widest">Inversión sugerida total</span>
                <span className="font-display text-2xl" data-testid="digest-total-cost">{formatCurrency(digestData.total_cost)}</span>
              </div>
              {digestData.alerts_count > 0 && (
                <p className="text-sm text-[#e11d48]">⚠ {digestData.alerts_count} alertas activas requieren atención.</p>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDigestOpen(false)}>Cerrar</Button>
            <Button
              onClick={sendDigest}
              disabled={sendingDigest || !digestData || digestData.top_items.length === 0}
              className="bg-[#3e166c] hover:bg-[#3e166c]/90"
              data-testid="digest-send-button"
            >
              <Send className="h-4 w-4 mr-2" />
              {sendingDigest ? "Enviando…" : "Enviar a mi correo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <Card className="p-4 bg-white border-[#b182ec]/30">
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-widest text-[#7248ae]">{label}</p>
        <Icon className="h-4 w-4" style={{ color }} />
      </div>
      <p className="font-display text-xl mt-1 text-[#1a0f2e]">{value}</p>
    </Card>
  );
}
